/**
 * @typedef {Object} ErrorFilter
 * @property {string[]} messageIncludes - substrings that must appear in the error message
 * @property {string[]} [componentIncludes] - optional substrings to match component stack
 */

/**
 * Usage example:
 *
 * In this plugin, you can selectively ignore Vue errors by specifying substrings
 * to match in the error message and optionally in the component stack.
 *
 * Example configuration:
 *
 * const ignoredErrors: ErrorFilter[] = [
 *   {
 *     // Ignore any error message that contains this substring
 *     messageIncludes: [
 *       'Property "value" was accessed during render but is not defined on instance',
 *     ],
 *     // Only ignore if these components also appear in the stack trace
 *     componentIncludes: ['<LocaleReceiver', '<NuxtRoot>']
 *   },
 *   {
 *     // Ignore any error that contains this substring in the message, regardless of component
 *     messageIncludes: ['Another error substring to ignore'],
 *   }
 * ]
 *
 * Behavior:
 * - If a error matches all messageIncludes and all componentIncludes (if specified), it is suppressed.
 * - All other errors are displayed exactly as Vue would normally show them.
 */

const originalError = console.error

// Configure ignored errors here
const ignoredErrors = [
  {
    messageIncludes: [
      'PDFLinkService.navigateTo: "null" is not a valid destination array',
    ]
  },
]

console.error = (...args) => {
  if (!args.length || typeof args[0] !== 'string') {
    originalError(...args)
    return
  }

  // Flatten all arguments into a single string for pattern matching
  const fullMessage = args
    .map(a => (typeof a === 'string' ? a : JSON.stringify(a)))
    .join('\n')

  // Check if any ignored error pattern matches
  const shouldIgnore = ignoredErrors.some(pattern => {
    const messageMatches = pattern.messageIncludes.every(sub => fullMessage.includes(sub))
    const componentMatches = pattern.componentIncludes
      ? pattern.componentIncludes.every(sub => fullMessage.includes(sub))
      : true

    return messageMatches && componentMatches
  })

  if (shouldIgnore) return

  // Otherwise, output exactly as Vue would
  originalError(...args)
}

export default {}